/**
 * Opponent Dice Manager
 *
 * Manages dice for other players:
 * 1. When opponent joins: Create ALL dice upfront (3 for Cee-Lo) - ONCE
 * 2. Activate/deactivate dice based on game phase (HI_LO=1, BANKER_ROLL=3)
 * 3. Never destroy dice until opponent leaves
 *
 * Uses real Dice objects with Cannon-ES physics for accurate simulation.
 */

import * as THREE from 'three';
import gsap from 'gsap';
import { DiceSyncPayload, DiceThrowParams, DicePhase } from '@sdl/shared';
import { Engine } from '../../engine/Engine';
import { Dice } from './Dice';
import { sound, SoundId } from '../../engine/audio/sound';
import { TSS } from '../../tss/TSS';
import type { UINodeConfig } from '../../tss/types';
import type { DiceAssetManager, DiceAsset } from './assets/DiceAssetManager';
import { OpponentDragConfig } from '../config/OpponentDragConfig';

/** Max dice per opponent (Cee-Lo uses 3) */
const MAX_DICE_COUNT = 3;
const _worldPosition = new THREE.Vector3();

/** Player info for display labels */
export interface PlayerInfo {
  username: string;
  avatar?: string;
  equippedDice?: string[];
}

interface OpponentDie {
  dice: Dice;
  active: boolean;  // true = in scene, false = not in scene
  asset: DiceAsset | null;
}

/**
 * One historical drag snapshot kept in the per-opponent interpolation buffer.
 * `time` is the LOCAL arrival timestamp (Date.now()) — using arrival time
 * instead of server timestamp avoids client/server clock-skew problems.
 */
interface DragSnapshot {
  position: { x: number; y: number; z: number };
  time: number;
}

interface OpponentState {
  playerId: string;
  playerInfo: PlayerInfo;         // Username and avatar for display
  dice: OpponentDie[];            // All dice for this opponent (always MAX_DICE_COUNT)
  heldDice: Dice[];               // Dice currently being dragged
  activeDiceCount: number;        // How many dice are active for current phase
  targetPosition: THREE.Vector3;
  targetRotation: THREE.Quaternion;
  phase: DicePhase;               // Server-authoritative dice phase
  lastDragSeq: number;
  lastDragTimestamp: number;
  /** Ring of recent drag snapshots, oldest first. The update loop interpolates
   *  between buffer[i] and buffer[i+1] at renderTime = now - INTERPOLATION_DELAY_MS. */
  dragBuffer: DragSnapshot[];
  dragGroup: THREE.Group | null;  // Group that holds dragging dice + label (single lerp target)
  dragLabel: TSS | null; // Billboard label shown during dragging
  resultLabel: TSS | null; // Billboard label shown above stopped dice during HI_LO
  readyLabel: TSS | null; // Billboard label shown above resting dice before opponent drags
  equippedDice: string[];         // Opponent's equipped dice IDs for asset loading
}

export class OpponentDiceManager {
  private engine: Engine;
  private opponents: Map<string, OpponentState> = new Map();
  private localPlayerId: string | null = null;
  private assetManager: DiceAssetManager | null = null;

  // Interpolation settings for smooth drag following
  /** Client-side interpolation delay for opponent drag. Render the dragGroup
   *  at "now - INTERPOLATION_DELAY_MS" by interpolating between buffered
   *  snapshots. 100ms gives ~3 buffered packets at the 30Hz server rate —
   *  enough headroom that one delayed/dropped packet doesn't cause stalls.
   *  This trades 100ms of "display lag" for mathematically smooth motion
   *  (no exponential lerp easing, no jitter from packet timing variance).
   *  Canonical FPS-netcode pattern (Source/Quake/Unity entity interpolation).
   */
  // Live-tunable via OpponentDragConfig (read every frame in update()).
  private readonly STALE_TIMEOUT_MS = 800;
  private readonly HOLD_SPACING = 1.2;

  // ── Avatar images for 3D labels (shared cache, keyed by URL) ──
  /** Avatar URLs load into HTMLImageElements; the TSS circle draws the image
   *  (never the raw URL — that was the "filename shows instead of photo" bug). */
  private static avatarImages = new Map<string, HTMLImageElement>();
  private static avatarLoadListeners = new Set<() => void>();

  private static loadAvatar(url: string): HTMLImageElement {
    let img = OpponentDiceManager.avatarImages.get(url);
    if (img) return img;
    img = new Image();
    // CORS is REQUIRED — the image is drawn to a canvas that becomes a WebGL
    // texture. A cross-origin image without CORS taints the canvas and the
    // texture upload throws (SecurityError), breaking the whole label. With
    // this, the canvas stays clean (CDN must send Access-Control-Allow-Origin).
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      for (const l of OpponentDiceManager.avatarLoadListeners) l();
    };
    // If CORS/load fails, naturalWidth stays 0 → avatarCircle falls back to the
    // initial, so the label still renders (just without the photo).
    img.onerror = () => { /* keep initial fallback */ };
    img.src = url;
    OpponentDiceManager.avatarImages.set(url, img);
    return img;
  }

  /**
   * Resolve a player's avatar to a TSS circle's image/text. A URL renders as a
   * loaded image; anything else (emoji, empty) falls back to the username
   * initial. NEVER returns the raw URL as text.
   */
  private avatarCircle(info: PlayerInfo): { image?: HTMLImageElement; text?: string } {
    const raw = (info.avatar ?? '').trim();
    const initial = info.username.trim().charAt(0).toUpperCase() || '?';
    const isUrl = /^https?:\/\//i.test(raw) || raw.startsWith('/');
    if (!isUrl) return { text: raw || initial };
    const img = OpponentDiceManager.loadAvatar(raw);
    if (img.complete && img.naturalWidth > 0) return { image: img };
    return { text: initial }; // still loading — show initial, redraw on load
  }

  /** Start loading a player's avatar URL (if any) into the shared cache. */
  private preloadAvatar(info?: PlayerInfo): void {
    const raw = (info?.avatar ?? '').trim();
    if (/^https?:\/\//i.test(raw) || raw.startsWith('/')) OpponentDiceManager.loadAvatar(raw);
  }

  /** Rebuild every live label so a just-loaded avatar image gets picked up. */
  private redrawLabels = (): void => {
    for (const state of this.opponents.values()) {
      if (state.dragLabel) { state.dragLabel.setTree(this.dragLabelTree(state.playerInfo)); state.dragLabel.render(); }
      if (state.readyLabel) { state.readyLabel.setTree(this.dragLabelTree(state.playerInfo)); state.readyLabel.render(); }
      if (state.resultLabel) { state.resultLabel.setTree(this.resultLabelTree(state.playerInfo)); state.resultLabel.render(); }
    }
  };

  constructor(engine: Engine) {
    this.engine = engine;
    OpponentDiceManager.avatarLoadListeners.add(this.redrawLabels);
  }

  setLocalPlayerId(playerId: string | null): void {
    this.localPlayerId = playerId;
  }

  setAssetManager(manager: DiceAssetManager | null): void {
    this.assetManager = manager;
  }

  /**
   * Update opponent's dice phase from server broadcast
   * Server is the source of truth for when dice should be visible
   */
  setOpponentPhase(playerId: string, phase: DicePhase): void {
    const state = this.opponents.get(playerId);
    if (state) {
      state.phase = phase;
    }
  }

  /**
   * Set how many dice should be active for this opponent
   * Call this when game phase changes (HI_LO=1, BANKER_ROLL=3)
   */
  setActiveDiceCount(playerId: string, count: number): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);
    if (!state) {
      // Auto-load if not exists
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    const newCount = Math.min(count, MAX_DICE_COUNT);
    if (state.activeDiceCount !== newCount) {
      state.activeDiceCount = newCount;
    }
  }

  /**
   * Ensure opponent has correct active dice count for current game state
   * Alias for setActiveDiceCount for backward compatibility
   */
  ensureOpponentDiceCount(playerId: string, expectedCount: number): void {
    this.setActiveDiceCount(playerId, expectedCount);
  }

  /**
   * Get opponent's equipped dice IDs
   */
  getOpponentEquippedDice(playerId: string): string[] {
    return this.opponents.get(playerId)?.equippedDice ?? [];
  }

  /**
   * Get opponent's current dice phase
   */
  getOpponentPhase(playerId: string): DicePhase {
    return this.opponents.get(playerId)?.phase ?? DicePhase.IDLE;
  }

  /**
   * Get opponent's active dice (for camera tracking)
   */
  getOpponentActiveDice(playerId: string): Dice[] {
    const state = this.opponents.get(playerId);
    if (!state) return [];

    return state.dice
      .slice(0, state.activeDiceCount)
      .filter((die) => die.active)
      .map((die) => die.dice);
  }

  /**
   * Get active dice from all opponents whose phase is STOPPED (have a result).
   * Used during HI_LO to frame all result dice in the camera.
   */
  getAllStoppedDice(): Dice[] {
    const result: Dice[] = [];
    for (const state of this.opponents.values()) {
      if (state.phase !== DicePhase.STOPPED) continue;
      for (let i = 0; i < state.activeDiceCount; i++) {
        if (state.dice[i]?.active) {
          result.push(state.dice[i].dice);
        }
      }
    }
    return result;
  }

  /** Get ALL active dice across all opponents (any phase) */
  getAllActiveDice(): Dice[] {
    const result: Dice[] = [];
    for (const state of this.opponents.values()) {
      for (let i = 0; i < state.activeDiceCount; i++) {
        if (state.dice[i]?.active) {
          result.push(state.dice[i].dice);
        }
      }
    }
    return result;
  }

  /**
   * Show a name label floating above an opponent's first active die.
   * Used during HI_LO to identify whose die is whose.
   */
  /** Result label tree (avatar circle + name) shown above stopped HI_LO dice. */
  private resultLabelTree(info: PlayerInfo): UINodeConfig {
    return {
      tag: 'root',
      style: {
        direction: 'row',
        alignItems: 'center',
        padding: [8, 12, 8, 8],
        gap: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderRadius: 10,
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
      },
      children: [
        {
          tag: 'avatar',
          style: { color: '#FFFFFF', fontFamily: 'sans-serif' },
          circle: {
            radius: 18,
            fill: 'rgba(255, 215, 0, 0.15)',
            stroke: 'rgba(255, 215, 0, 0.6)',
            strokeWidth: 2,
            ...this.avatarCircle(info),
            textSize: 20,
          },
        },
        {
          tag: 'name',
          text: info.username,
          style: {
            fontWeight: 'bold',
            fontSize: 18,
            fontFamily: 'Oswald, sans-serif',
            color: '#FFFFFF',
            maxWidth: 160,
            overflow: 'ellipsis',
          },
        },
      ],
    };
  }

  showResultLabel(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state) return;

    // Find the first active die to position above
    const activeDie = state.dice.find((d, i) => d.active && i < state.activeDiceCount);
    if (!activeDie) return;

    // Reuse existing label if present
    if (state.resultLabel) {
      const pos = activeDie.dice.getPosition();
      state.resultLabel.object3D.position.set(pos.x, pos.y + 1.5, pos.z);
      state.resultLabel.object3D.visible = true;
      return;
    }

    const info = state.playerInfo;
    if (!info) return;

    const label = new TSS(
      this.resultLabelTree(info),
      {
        worldHeight: 0.5,
        mode: 'sprite',
        resolution: 1,
        depthTest: false,
        depthWrite: false,
      }
    );

    label.render();
    const pos = activeDie.dice.getPosition();
    label.object3D.position.set(pos.x, pos.y + 1.5, pos.z);
    this.engine.scene.add(label.object3D);

    state.resultLabel = label;
  }

  /** Hide and dispose result label for an opponent */
  hideResultLabel(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state?.resultLabel) return;
    state.resultLabel.object3D.removeFromParent();
    state.resultLabel.dispose();
    state.resultLabel = null;
  }

  /** Hide all result labels */
  hideAllResultLabels(): void {
    for (const [playerId] of this.opponents) {
      this.hideResultLabel(playerId);
    }
  }

  /**
   * Apply phases from server state (for reconnection)
   */
  applyPlayerRollStates(playerRollStates: Record<string, { phase: DicePhase; throwParams: unknown; result: unknown }>): void {
    for (const [playerId, rollState] of Object.entries(playerRollStates)) {
      if (playerId === this.localPlayerId) continue;

      const state = this.opponents.get(playerId);
      if (state) {
        state.phase = rollState.phase;
        if ((rollState as any).diceCount !== undefined) {
          state.activeDiceCount = Math.min((rollState as any).diceCount ?? MAX_DICE_COUNT, MAX_DICE_COUNT);
        }
      }
    }
  }

  /**
   * Hydrate drag pose from snapshot (reconnection/late join)
   */
  hydrateDragSnapshot(playerId: string, dragPose: { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number; w: number }; seq: number; timestamp: number }, diceCount: number): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);
    if (!state) {
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    state.activeDiceCount = Math.min(diceCount, MAX_DICE_COUNT);
    state.phase = DicePhase.COLLECTING;
    state.lastDragSeq = dragPose.seq;
    state.lastDragTimestamp = dragPose.timestamp;
    state.targetPosition.set(dragPose.position.x, dragPose.position.y, dragPose.position.z);
    state.targetRotation.set(dragPose.rotation.x, dragPose.rotation.y, dragPose.rotation.z, dragPose.rotation.w);

    // Activate and pick up dice for immediate visibility
    state.heldDice = [];
    for (let i = 0; i < state.activeDiceCount && i < state.dice.length; i++) {
      const opponentDie = state.dice[i];
      if (!opponentDie.active) {
        this.engine.scene.add(opponentDie.dice.mesh);
        opponentDie.active = true;
      }
      opponentDie.dice.pickup();
      const offsetX = (i - (state.activeDiceCount - 1) / 2) * this.HOLD_SPACING;
      opponentDie.dice.setPosition({
        x: dragPose.position.x + offsetX,
        y: dragPose.position.y,
        z: dragPose.position.z,
      });
      opponentDie.dice.mesh.quaternion.set(
        dragPose.rotation.x,
        dragPose.rotation.y,
        dragPose.rotation.z,
        dragPose.rotation.w,
      );
      opponentDie.dice.body.quaternion.set(
        dragPose.rotation.x,
        dragPose.rotation.y,
        dragPose.rotation.z,
        dragPose.rotation.w,
      );
      state.heldDice.push(opponentDie.dice);
    }
  }

  /**
   * Load an opponent - create ALL dice upfront (called once when opponent joins).
   * Tries to load custom dice skins via DiceAssetManager; falls back to red boxes.
   */
  loadOpponent(playerId: string, playerInfo?: PlayerInfo): void {
    // Skip local player
    if (playerId === this.localPlayerId) return;

    // Warm the avatar image cache early so the 3D label shows the photo
    // (not the initial) the first time it appears.
    this.preloadAvatar(playerInfo);

    // Check if already loaded
    if (this.opponents.has(playerId)) {
      // Update player info if provided (for late-join scenarios)
      if (playerInfo) {
        const state = this.opponents.get(playerId)!;
        state.playerInfo = playerInfo;
      }
      return;
    }

    const equippedDice = playerInfo?.equippedDice ?? [];

    // Create ALL dice for this opponent (always MAX_DICE_COUNT)
    const dice: OpponentDie[] = [];
    for (let i = 0; i < MAX_DICE_COUNT; i++) {
      const diceId = equippedDice[i] ?? null;
      let asset: DiceAsset | null = null;

      // Try to acquire a custom dice asset if already preloaded
      if (diceId && this.assetManager?.isReady(diceId)) {
        asset = this.assetManager.acquireDice(diceId);
      }

      const die = new Dice(this.engine.physics, this.engine.scene, {
        position: { x: 0, y: 4, z: 5 },
        size: 1,
        color: 0xcc0000, // Fallback color (only used if no asset)
      }, asset ?? undefined);
      // Remove from scene - not active yet
      this.engine.scene.remove(die.mesh);
      // Start in held mode (physics disabled)
      die.pickup();
      dice.push({ dice: die, active: false, asset });
    }

    this.opponents.set(playerId, {
      playerId,
      playerInfo: playerInfo ?? { username: 'Player', avatar: '🎲' },
      dice,
      heldDice: [],
      activeDiceCount: MAX_DICE_COUNT, // Default to max, will be adjusted by game state
      targetPosition: new THREE.Vector3(),
      targetRotation: new THREE.Quaternion(),
      phase: DicePhase.IDLE,
      lastDragSeq: -1,
      lastDragTimestamp: 0,
      dragBuffer: [],
      dragGroup: null,
      dragLabel: null,
      resultLabel: null,
      readyLabel: null,
      equippedDice,
    });

    // If any dice still need loading, preload in background and upgrade when ready
    const unloadedIds = equippedDice.filter((id, i) => id && !dice[i].asset);
    if (unloadedIds.length > 0 && this.assetManager) {
      this.assetManager.preload(unloadedIds).then(() => {
        this.upgradeOpponentDice(playerId);
      }).catch((err) => {
        console.warn('[OpponentDice] background preload failed', err);
      });
    }
  }

  /**
   * Upgrade opponent dice from fallback red boxes to custom assets
   * after background preload completes. Only upgrades inactive dice.
   */
  private upgradeOpponentDice(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state || !this.assetManager) return;

    for (let i = 0; i < state.dice.length; i++) {
      const opponentDie = state.dice[i];
      const diceId = state.equippedDice[i] ?? null;

      // Skip if already has asset or no dice ID
      if (opponentDie.asset || !diceId) continue;
      if (!this.assetManager.isReady(diceId)) continue;

      const asset = this.assetManager.acquireDice(diceId);
      if (!asset) continue;

      // Only upgrade if die is inactive (not currently in scene)
      if (opponentDie.active) {
        this.assetManager.releaseDice(asset);
        continue;
      }

      // Dispose old procedural die, create new one with asset
      opponentDie.dice.dispose();
      const newDie = new Dice(this.engine.physics, this.engine.scene, {
        position: { x: 0, y: 4, z: 5 },
        size: 1,
      }, asset);
      this.engine.scene.remove(newDie.mesh);
      newDie.pickup();
      opponentDie.dice = newDie;
      opponentDie.asset = asset;
    }

  }

  /**
   * Create a billboard label for showing player info during drag.
   * Uses TSS: avatar circle (left) + username (right)
   */
  /** Drag/ready label tree (avatar circle + name). */
  private dragLabelTree(playerInfo: PlayerInfo): UINodeConfig {
    return {
      tag: 'root',
      style: {
        direction: 'row',
        alignItems: 'center',
        padding: [10, 14, 10, 10],
        gap: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderRadius: 12,
        borderColor: 'rgba(255, 215, 0, 0.6)',
        borderWidth: 2,
      },
      children: [
        {
          tag: 'avatar',
          style: { color: '#FFFFFF', fontFamily: 'sans-serif' },
          circle: {
            radius: 22,
            fill: 'rgba(255, 215, 0, 0.15)',
            stroke: 'rgba(255, 215, 0, 0.6)',
            strokeWidth: 2,
            ...this.avatarCircle(playerInfo),
            textSize: 24,
          },
        },
        {
          tag: 'name',
          text: playerInfo.username,
          style: {
            fontWeight: 'bold',
            fontSize: 20,
            fontFamily: 'Oswald, sans-serif',
            color: '#FFD700',
            maxWidth: 180,
            overflow: 'ellipsis',
          },
        },
      ],
    };
  }

  private createDragLabel(playerInfo: PlayerInfo): TSS {
    return new TSS(
      this.dragLabelTree(playerInfo),
      {
        worldHeight: 0.625,
        mode: 'sprite',
        resolution: 1,
        depthTest: false,
        depthWrite: false,
      }
    );
  }

  /**
   * Show an opponent's dice at their rest positions with a name label above,
   * before they start dragging. Mirrors the local player's "PICK UP DICE" layout
   * but replaces per-die pickup tags with a single player identity pill.
   *
   * Called when it becomes the opponent's turn to roll (BANKER_ROLL / PLAYER_ROLL).
   * Transitions to drag group automatically on first dice:sync (handleDiceSync
   * disposes the ready label before setupDragGroup runs).
   */
  showReadyDice(playerId: string, positions: Array<{ x: number; y: number; z: number }>): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);
    if (!state) {
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    // If opponent is already dragging or past drag (thrown/stopped), don't
    // retroactively show a ready label — that would conflict with the drag group.
    if (state.dragGroup || state.heldDice.length > 0) return;
    if (state.phase === DicePhase.THROWN || state.phase === DicePhase.STOPPED) return;

    const count = Math.min(positions.length, state.dice.length);

    // Activate the first `count` dice at the given world positions (in scene root,
    // not a group — they sit at rest, no lerp target).
    for (let i = 0; i < count; i++) {
      const opponentDie = state.dice[i];
      const pos = positions[i];
      opponentDie.dice.setPosition(pos);
      // Reset rotation to identity so dice sit flat — a die carrying rotation
      // from its previous landed pose would appear tilted against its neighbors.
      opponentDie.dice.setRotationFromQuaternion({ x: 0, y: 0, z: 0, w: 1 });
      if (!opponentDie.active) {
        this.engine.scene.add(opponentDie.dice.mesh);
        opponentDie.active = true;
      }
      // Keep physics disabled — dice sit still until opponent picks them up.
      opponentDie.dice.pickup();
    }

    // Reuse existing label if present (phase re-entry shouldn't recreate)
    if (state.readyLabel) {
      this.positionReadyLabel(state, positions);
      state.readyLabel.object3D.visible = true;
      return;
    }

    const label = this.createDragLabel(state.playerInfo);
    label.render();
    this.engine.scene.add(label.object3D);
    state.readyLabel = label;
    this.positionReadyLabel(state, positions);
  }

  /** Position the ready label above the geometric centroid of the provided positions. */
  private positionReadyLabel(state: OpponentState, positions: Array<{ x: number; y: number; z: number }>): void {
    if (!state.readyLabel || positions.length === 0) return;
    // Geometric mean of positions — for 2 dice this lands the label
    // between them (matching the dragGroup label which sits at the
    // group origin = midpoint). Picking positions[Math.floor(n/2)]
    // worked for 3 dice (middle die IS the center) but for 2 dice it
    // returned the right die, leaving the label visibly off-center.
    let cx = 0, cy = 0, cz = 0;
    for (const p of positions) { cx += p.x; cy += p.y; cz += p.z; }
    cx /= positions.length; cy /= positions.length; cz /= positions.length;
    state.readyLabel.object3D.position.set(cx, cy + 1.8, cz);
  }

  /** Hide and dispose the ready label + deactivate ready-state dice */
  hideReadyDice(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state) return;
    if (state.readyLabel) {
      state.readyLabel.object3D.removeFromParent();
      state.readyLabel.dispose();
      state.readyLabel = null;
    }
  }

  /**
   * Set up drag group for an opponent (called when dragging starts)
   * Creates a group that holds both dice meshes and label - only this group is lerped
   */
  private setupDragGroup(state: OpponentState): void {
    if (state.dragGroup) return; // Already set up

    // Create main drag group - this is the only thing we lerp
    state.dragGroup = new THREE.Group();
    this.engine.scene.add(state.dragGroup);

    // Create and add the label
    state.dragLabel = this.createDragLabel(state.playerInfo);
    state.dragLabel.object3D.position.set(0, 2, 0); // Position above dice (relative to group)
    state.dragGroup.add(state.dragLabel.object3D);

  }

  /**
   * Add dice meshes to the drag group with proper offsets
   * Dice are parented to the group so they move together
   */
  private addDiceToDragGroup(state: OpponentState): void {
    if (!state.dragGroup) return;

    for (let i = 0; i < state.heldDice.length; i++) {
      const die = state.heldDice[i];
      const offsetX = (i - (state.heldDice.length - 1) / 2) * this.HOLD_SPACING;

      // Remove from scene root (if present)
      this.engine.scene.remove(die.mesh);

      // Add to drag group with offset position
      state.dragGroup.add(die.mesh);
      die.mesh.position.set(offsetX, 0, 0);
    }
  }

  /**
   * Remove dice meshes from drag group back to scene root (for physics simulation)
   * Note: We don't preserve transforms here - handleThrow will set exact values from throwParams
   */
  private removeDiceFromDragGroup(state: OpponentState): void {
    if (!state.dragGroup) return;

    for (const die of state.heldDice) {
      // Simply reparent to scene - transforms will be set by handleThrow
      state.dragGroup.remove(die.mesh);
      this.engine.scene.add(die.mesh);
    }
  }

  /**
   * Clean up drag group (called when dragging ends)
   */
  private cleanupDragGroup(state: OpponentState): void {
    // First remove dice from group back to scene
    this.removeDiceFromDragGroup(state);

    // Dispose label
    if (state.dragLabel) {
      state.dragLabel.dispose();
      state.dragLabel = null;
    }

    // Remove group from scene
    if (state.dragGroup) {
      this.engine.scene.remove(state.dragGroup);
      state.dragGroup = null;
    }

  }

  /**
   * Deactivate all dice for this opponent (remove from scene)
   */
  private deactivateAllDice(state: OpponentState): void {
    // Clean up drag group first (removes dice from group, disposes label)
    this.cleanupDragGroup(state);

    // Dispose any ready-state label (opponent never dragged before transition)
    if (state.readyLabel) {
      state.readyLabel.object3D.removeFromParent();
      state.readyLabel.dispose();
      state.readyLabel = null;
    }

    const activeCount = state.dice.filter(d => d.active).length;
    if (activeCount > 0) {
      console.warn('[OpponentDice] deactivateAllDice REMOVING dice', {
        playerId: state.playerId,
        phase: state.phase,
        activeCount,
        stack: new Error().stack?.split('\n').slice(1, 6).join(' | '),
      });
    }

    for (const opponentDie of state.dice) {
      if (opponentDie.active) {
        this.engine.scene.remove(opponentDie.dice.mesh);
        opponentDie.dice.pickup();
        opponentDie.active = false;
      }
    }
    state.heldDice = [];
    // Drop any buffered drag snapshots — otherwise a re-pickup next round
    // would briefly interpolate from the previous round's stale positions.
    state.dragBuffer.length = 0;
  }

  /**
   * Handle dice sync during drag - pick up dice from pool and sync position
   * Only show dice if server has confirmed opponent is COLLECTING or HOLDING
   */
  handleDiceSync(data: DiceSyncPayload): void {
    for (const playerData of data.players) {
      // Skip local player
      if (playerData.playerId === this.localPlayerId) continue;

      let state = this.opponents.get(playerData.playerId);

      // Auto-load opponent if not exists
      if (!state) {
        this.loadOpponent(playerData.playerId);
        state = this.opponents.get(playerData.playerId)!;
      }

      // Only show dice if server has confirmed opponent is actively collecting/holding
      if (state.phase !== DicePhase.COLLECTING && state.phase !== DicePhase.HOLDING) {
        continue;
      }

      // Sequence check to avoid out-of-order/stale updates
      if (playerData.seq !== undefined && playerData.seq <= state.lastDragSeq) {
        continue;
      }

      state.lastDragSeq = playerData.seq ?? state.lastDragSeq;
      state.lastDragTimestamp = playerData.timestamp ?? Date.now();

      // Push to interpolation buffer with LOCAL arrival time. Update loop
      // interpolates between snapshots at renderTime = now - INTERPOLATION_DELAY_MS.
      // Trim snapshots older than 2× delay so the buffer never grows unbounded.
      const arrivalMs = Date.now();
      state.dragBuffer.push({
        position: { x: playerData.position.x, y: playerData.position.y, z: playerData.position.z },
        time: arrivalMs,
      });
      const trimThreshold = arrivalMs - 2 * OpponentDragConfig.interpolationDelayMs;
      while (state.dragBuffer.length > 2 && state.dragBuffer[0].time < trimThreshold) {
        state.dragBuffer.shift();
      }

      // Keep targetPosition as the latest known target (used as fallback when
      // renderTime exceeds the newest buffered snapshot, e.g. opponent stopped).
      state.targetPosition.set(
        playerData.position.x,
        playerData.position.y,
        playerData.position.z
      );
      state.targetRotation.set(
        playerData.rotation.x,
        playerData.rotation.y,
        playerData.rotation.z,
        playerData.rotation.w
      );

      // Pick up all active dice during collection
      // For BANKER_ROLL/PLAYER_ROLL this is 3 dice, for HI_LO this is 1 die
      if (state.heldDice.length < state.activeDiceCount) {
        const isFirstPickup = state.heldDice.length === 0;

        // Set up drag group on first pickup (creates group + label)
        if (isFirstPickup) {
          // Dispose any ready-state label — drag group owns the identity pill now
          if (state.readyLabel) {
            state.readyLabel.object3D.removeFromParent();
            state.readyLabel.dispose();
            state.readyLabel = null;
          }
          // Dispose any HI_LO result label parked at the previous stopped die
          // position — the drag label that setupDragGroup creates follows the
          // dice being grabbed. Leaving the result label stranded is the visual
          // bug where the player's name hovers above the old dice location.
          if (state.resultLabel) {
            state.resultLabel.object3D.removeFromParent();
            state.resultLabel.dispose();
            state.resultLabel = null;
          }
          this.setupDragGroup(state);
          // Position group at target
          if (state.dragGroup) {
            state.dragGroup.position.copy(state.targetPosition);
          }
        }

        // Activate and pick up all active dice
        for (let i = 0; i < state.activeDiceCount && i < state.dice.length; i++) {
          const opponentDie = state.dice[i];
          if (!state.heldDice.includes(opponentDie.dice)) {
            opponentDie.active = true;
            opponentDie.dice.pickup();
            state.heldDice.push(opponentDie.dice);
          }
        }

        // Add dice to drag group (positions them relative to group center)
        if (isFirstPickup) {
          this.addDiceToDragGroup(state);
        }
      }
    }
  }

  /**
   * Handle opponent collecting more dice
   */
  handleDicePickup(playerId: string, diceIndex: number): void {
    if (playerId === this.localPlayerId) return;

    const state = this.opponents.get(playerId);
    if (!state) return;

    if (diceIndex < state.dice.length && diceIndex < state.activeDiceCount) {
      const opponentDie = state.dice[diceIndex];
      if (!state.heldDice.includes(opponentDie.dice)) {
        if (!opponentDie.active) {
          this.engine.scene.add(opponentDie.dice.mesh);
          opponentDie.active = true;
        }
        opponentDie.dice.pickup();
        state.heldDice.push(opponentDie.dice);
        // Quieter than the local pickup — another player's collecting reads
        // as background table noise, not your own action.
        sound.play(SoundId.DICE_PICKUP, { volume: 0.6 });
      }
    }
  }

  /**
   * Handle opponent throw - apply physics to the active dice
   */
  handleThrow(playerId: string, throwParams: DiceThrowParams): void {
    if (playerId === this.localPlayerId) return;

    let state = this.opponents.get(playerId);

    // Auto-load if not exists
    if (!state) {
      this.loadOpponent(playerId);
      state = this.opponents.get(playerId)!;
    }

    const diceCount = throwParams.diceStates.length;

    // Update active dice count to match throw
    state.activeDiceCount = diceCount;

    // Clean up drag group first - removes dice from group back to scene root
    this.cleanupDragGroup(state);

    // Ensure all dice being thrown are active and in scene root
    for (let i = 0; i < diceCount && i < state.dice.length; i++) {
      const opponentDie = state.dice[i];

      // Add to scene if not already there
      if (!opponentDie.dice.mesh.parent || opponentDie.dice.mesh.parent !== this.engine.scene) {
        this.engine.scene.add(opponentDie.dice.mesh);
      }
      opponentDie.active = true;
    }

    // Apply physics to all dice being thrown
    for (let i = 0; i < diceCount && i < state.dice.length; i++) {
      const diceState = throwParams.diceStates[i];
      const die = state.dice[i].dice;

      // Fully reset physics state
      die.body.velocity.setZero();
      die.body.angularVelocity.setZero();
      die.body.force.setZero();
      die.body.torque.setZero();

      // Reset mesh transform (clear any local offsets from drag group)
      die.mesh.position.set(0, 0, 0);
      die.mesh.quaternion.identity();
      die.mesh.updateMatrixWorld(true);

      // Route through setPosition / setRotationFromQuaternion so the physics
      // interpolation state is refreshed alongside the body + mesh — otherwise
      // the first render frame after release lerps from stale pose to throw pose.
      die.setPosition(diceState.position);
      die.setRotationFromQuaternion(diceState.rotation);
      die.setVelocity(diceState.velocity);
      die.setAngularVelocity(diceState.angularVelocity);

      // Release - enables physics
      die.release();
    }

    // Clear held dice + drag buffer after throw (they're now rolling).
    // Stale snapshots would otherwise leak into the next pickup's interpolation
    // and pull dice toward the previous round's final drag pose.
    state.heldDice = [];
    state.dragBuffer.length = 0;
  }

  /**
   * Update opponent dice (call every frame)
   * 
   * Performance: With 5 opponents × 3 dice, we only do 5 position lerps (one per drag group)
   * instead of 15 individual dice lerps + 5 label lerps
   * 
   * Design: During drag, only position needs to match the opponent.
   * - Label stays upright (billboard, always faces camera)
   * - Dice rotate independently on client (cosmetic tumble effect)
   * - Actual rotation only matters at throw time (from throwParams)
   */
  update(deltaTime: number): void {
    for (const state of this.opponents.values()) {
      // Only lerp the drag group POSITION - dice are children so they move automatically
      // This is O(n) for n opponents instead of O(n*dice) for individual lerps
      // NOTE: We do NOT rotate the group - label should stay upright
      if (state.dragGroup && state.heldDice.length > 0) {
        // Client-side interpolation: render the drag pose at "now - delay"
        // by interpolating between the two buffered snapshots that bracket
        // that timestamp. Smooth regardless of packet timing jitter.
        const renderTime = Date.now() - OpponentDragConfig.interpolationDelayMs;
        const buf = state.dragBuffer;
        const newest = buf.length > 0 ? buf[buf.length - 1] : null;
        const haveBracket = buf.length >= 2 && renderTime >= buf[0].time && newest !== null && renderTime <= newest.time;

        if (haveBracket) {
          // Find the snapshot pair [from, to] such that from.time <= renderTime <= to.time.
          // Linear scan from oldest is fine — buffer holds 3-6 entries typically.
          let fromIdx = 0;
          for (let i = 0; i < buf.length - 1; i++) {
            if (buf[i].time <= renderTime && buf[i + 1].time >= renderTime) {
              fromIdx = i;
              break;
            }
          }
          const from = buf[fromIdx];
          const to = buf[fromIdx + 1];
          const span = to.time - from.time;
          const alpha = span > 0 ? Math.max(0, Math.min(1, (renderTime - from.time) / span)) : 1;
          state.dragGroup.position.set(
            from.position.x + (to.position.x - from.position.x) * alpha,
            from.position.y + (to.position.y - from.position.y) * alpha,
            from.position.z + (to.position.z - from.position.z) * alpha,
          );
        } else if (buf.length > 0) {
          // No bracketing pair (renderTime past newest, or buffer just primed).
          // Exponentially follow the latest known target so we don't freeze
          // and then jump when the next packet lands.
          const k = 1 - Math.exp(-OpponentDragConfig.tailFollowRate * deltaTime);
          const p = state.dragGroup.position;
          p.x += (state.targetPosition.x - p.x) * k;
          p.y += (state.targetPosition.y - p.y) * k;
          p.z += (state.targetPosition.z - p.z) * k;
        }

        // Rotate dice individually for a nice tumble effect (purely cosmetic)
        // This is cheap - just incrementing rotation, no lerp target needed
        for (const die of state.heldDice) {
          die.mesh.rotation.x += deltaTime * 1.5;
          die.mesh.rotation.y += deltaTime * 2.0;
          die.mesh.getWorldPosition(_worldPosition);
          die.body.position.set(_worldPosition.x, _worldPosition.y, _worldPosition.z);
        }
      }

      // Hide stale drag if no updates received recently
      if (state.heldDice.length > 0 && Date.now() - state.lastDragTimestamp > this.STALE_TIMEOUT_MS) {
        this.deactivateAllDice(state);
      }

      // Sync rolling dice (active but not held) to physics
      // These are dice that have been thrown and are simulating
      for (const opponentDie of state.dice) {
        if (opponentDie.active && !state.heldDice.includes(opponentDie.dice) && !opponentDie.dice.held) {
          opponentDie.dice.syncToPhysics(deltaTime);
        }
      }

      // Edge-stuck dice are corrected by the shooter's authoritative finalStates
      // (sent with DicePhase.STOPPED via applyFinalStates). No local nudge here —
      // nudging during THROWN phase causes HI_LO physics divergence because each
      // client's die may appear to rest at slightly different times.
    }
  }

  /**
   * Apply shooter-authoritative final dice poses to correct any local divergence
   * (e.g. edge-stuck die that our local physics settled differently than the shooter).
   * Called on DICE_PHASE STOPPED with the shooter's post-nudge pose snapshot.
   */
  applyFinalStates(
    playerId: string,
    finalStates: Array<{
      position: { x: number; y: number; z: number };
      quaternion: { x: number; y: number; z: number; w: number };
    }>,
  ): void {
    if (playerId === this.localPlayerId) return;
    const state = this.opponents.get(playerId);
    if (!state) return;

    const count = Math.min(finalStates.length, state.activeDiceCount, state.dice.length);
    for (let i = 0; i < count; i++) {
      const pose = finalStates[i];
      const opponentDie = state.dice[i];
      if (!opponentDie.active) continue;
      const die = opponentDie.dice;

      // Disable physics so the lerp owns the dice until it completes.
      die.body.velocity.setZero();
      die.body.angularVelocity.setZero();
      die.body.force.setZero();
      die.body.torque.setZero();
      die.body.mass = 0;
      die.body.sleep();

      // Lerp position + rotation toward server-authoritative pose instead
      // of snapping. Small divergences (typical) become imperceptible;
      // large divergences (rare) become a brief glide rather than a teleport.
      const fromPos = die.mesh.position.clone();
      const targetPos = new THREE.Vector3(pose.position.x, pose.position.y, pose.position.z);
      const fromQuat = die.mesh.quaternion.clone();
      const targetQuat = new THREE.Quaternion(pose.quaternion.x, pose.quaternion.y, pose.quaternion.z, pose.quaternion.w);

      const tmpQuat = new THREE.Quaternion();
      const progress = { t: 0 };
      gsap.killTweensOf(progress);
      gsap.to(progress, {
        t: 1,
        duration: 0.25,
        ease: 'power2.out',
        onUpdate: () => {
          die.mesh.position.lerpVectors(fromPos, targetPos, progress.t);
          tmpQuat.copy(fromQuat).slerp(targetQuat, progress.t);
          die.mesh.quaternion.copy(tmpQuat);
          // Mirror onto the body so physics state stays consistent if anything
          // queries it mid-lerp.
          die.body.position.set(die.mesh.position.x, die.mesh.position.y, die.mesh.position.z);
          die.body.quaternion.set(tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w);
        },
      });
    }
  }

  /**
   * Reset opponent dice (after roll complete) - deactivate all dice
   */
  resetOpponent(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (!state) return;


    this.deactivateAllDice(state);
    this.hideResultLabel(playerId);
    state.phase = DicePhase.IDLE;
  }

  /**
   * Reset all opponent dice - useful when transitioning to new game phase
   */
  resetAllOpponents(): void {
    for (const playerId of this.opponents.keys()) {
      this.resetOpponent(playerId);
    }
  }

  /**
   * Remove opponent completely (when they leave the game)
   */
  removeOpponent(playerId: string): void {
    const state = this.opponents.get(playerId);
    if (state) {
      // Clean up drag group first (removes dice from group, disposes label)
      this.cleanupDragGroup(state);

      for (const opponentDie of state.dice) {
        // Release asset back to pool before disposing
        if (opponentDie.asset && this.assetManager) {
          this.assetManager.releaseDice(opponentDie.asset);
        }
        opponentDie.dice.dispose();
      }
      this.opponents.delete(playerId);
    }
  }

  /**
   * Clear all dice for a specific player (alias for removeOpponent)
   */
  clearPlayer(playerId: string): void {
    this.removeOpponent(playerId);
  }

  /**
   * Clear all opponent dice
   */
  clear(): void {
    for (const playerId of this.opponents.keys()) {
      this.removeOpponent(playerId);
    }
  }

  /**
   * Check if an opponent is loaded
   */
  hasOpponent(playerId: string): boolean {
    return this.opponents.has(playerId);
  }

  /**
   * Get opponent's active dice count
   */
  getOpponentDiceCount(playerId: string): number {
    return this.opponents.get(playerId)?.activeDiceCount ?? 0;
  }

  dispose(): void {
    OpponentDiceManager.avatarLoadListeners.delete(this.redrawLabels);
    this.clear();
  }
}
